import React, { useCallback, useMemo } from 'react';
import { useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import Header from '@components/DesignLibrary/Header';
import getDisplayValue from '@components/Questionnaire/form/List/getDisplayValue';
import Spacer from '@components/Spacer';
import s from './EditListItemModal.module.scss';

type Props = {
  currentListSubsection: string | null;
  field: Pick<QuestionnaireApiModels.FieldTemplate, 'label'>;
  linkSourceFieldKey: string;
  listSubsections: string[];
  modalSubFields: Array<
    & Pick<QuestionnaireApiModels.FieldTemplate, 'id' | 'label' | 'key' | 'isTitleField' | 'isSubtitleField'>
    & Parameters<typeof getDisplayValue>[0]
  >;
  onSelectSubsection: (subsection: string | null) => void;
  rowIndex: number;
};

const ModalFormHeader: React.FC<Props> = ({
  currentListSubsection,
  field,
  linkSourceFieldKey,
  listSubsections,
  modalSubFields,
  onSelectSubsection,
  rowIndex,
}) => {
  const {
    formMethods,
  } = useQuestionnaireFormContext();

  const titleField = useMemo(() => modalSubFields.find((f) => f.isTitleField), [modalSubFields]);
  const subtitleFields = useMemo(() => modalSubFields.filter((f) => f.isSubtitleField), [modalSubFields]);

  const getStringValue = useCallback((fieldKey: string | undefined | null): string | undefined => {
    if (!fieldKey) {
      return undefined;
    }
    const value = formMethods.getValues(`${linkSourceFieldKey}[${rowIndex}].${fieldKey}`);
    return typeof value === 'string' ? value : undefined;
  }, [linkSourceFieldKey, rowIndex, formMethods]);

  return (
    <Header
      title={getStringValue(titleField?.key) || field.label}
      titleTruncate = {true}
      description={
        <>
          <Spacer size={4} />
          <p className="caption">
            {subtitleFields.map((subtitleField, idx) => (
              <React.Fragment key={subtitleField.id}>
                <span style={{ color: 'var(--color-gray-75)' }}>{idx !== 0 ? ' · ' : ''}{subtitleField.label}: </span>
                <span style={{ color: 'var(--color-gray-100)' }}>
                  {getDisplayValue(subtitleField, getStringValue(subtitleField.key))}
                </span>
              </React.Fragment>
            ))}
          </p>
          <Spacer size={4} />
        </>
      }
      tabs={listSubsections.map((subsection) => ({
        label: subsection,
        onClick: () => onSelectSubsection(subsection === currentListSubsection ? null : subsection),
        selected: currentListSubsection === subsection,
      }))}
      className={s.modalHeader}
    />
  );
};

export default ModalFormHeader;